import { create } from 'zustand';

export type TwoFactorTriggerReason =
  | 'every_login'
  | 'new_device'
  | 'inactivity'
  | 'role_requires_setup';

interface PendingAuthState {
  userId: string;
  email: string;
  organizationId: string;
  triggerReason: TwoFactorTriggerReason;
  requires2FASetup: boolean;
}

interface PendingOwnerApproval {
  actionType: string;
  actionMetadata: Record<string, unknown>;
  description: string;
  onApproved: () => void;
  onDenied: () => void;
}

interface TwoFactorAuthState {
  // Login 2FA state
  pendingAuth: PendingAuthState | null;

  // Owner 2FA approval state
  pendingOwnerApproval: PendingOwnerApproval | null;
  isOwnerApprovalDialogOpen: boolean;

  // Actions
  setPendingAuth: (auth: PendingAuthState) => void;
  clearPendingAuth: () => void;

  requestOwnerApproval: (
    actionType: string,
    actionMetadata: Record<string, unknown>,
    description: string,
    onApproved: () => void,
    onDenied: () => void
  ) => void;
  clearOwnerApproval: () => void;
  setOwnerApprovalDialogOpen: (open: boolean) => void;
}

export const useTwoFactorAuthStore = create<TwoFactorAuthState>()((set) => ({
  // Initial state
  pendingAuth: null,
  pendingOwnerApproval: null,
  isOwnerApprovalDialogOpen: false,

  // Login 2FA actions
  setPendingAuth: (auth) =>
    set({
      pendingAuth: auth,
    }),

  clearPendingAuth: () =>
    set({
      pendingAuth: null,
    }),

  // Owner 2FA approval actions
  requestOwnerApproval: (actionType, actionMetadata, description, onApproved, onDenied) =>
    set({
      pendingOwnerApproval: {
        actionType,
        actionMetadata,
        description,
        onApproved,
        onDenied,
      },
      isOwnerApprovalDialogOpen: true,
    }),

  clearOwnerApproval: () =>
    set({
      pendingOwnerApproval: null,
      isOwnerApprovalDialogOpen: false,
    }),

  setOwnerApprovalDialogOpen: (open) =>
    set({
      isOwnerApprovalDialogOpen: open,
    }),
}));
